import { useCallback, useEffect, useState } from "react";
import type { DismissedDuplicate } from "../../../../types/api";
import {
  getDismissedDuplicates,
  restoreDismissedDuplicate,
} from "../../../../services/prospects";
import { getSafeErrorMessage } from "../../../../utils/errorHandler";
import { Button } from "../../../../components/Button";
import { memberName } from "./duplicateSelection";

interface DismissedDuplicatesPanelProps {
  /** Called after a restore so the parent refreshes the group list and badge count. */
  onRestored: () => void;
}

/**
 * Dismissal is permanent by design, which is exactly why it has to be recoverable:
 * without this view a mis-tap would hide a real duplicate forever.
 */
export const DismissedDuplicatesPanel = ({ onRestored }: DismissedDuplicatesPanelProps) => {
  const [dismissed, setDismissed] = useState<DismissedDuplicate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [restoringId, setRestoringId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError("");
    try {
      setDismissed(await getDismissedDuplicates());
    } catch (err) {
      setError(getSafeErrorMessage(err, "Failed to load dismissed duplicates"));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const handleRestore = async (dismissal: DismissedDuplicate) => {
    setRestoringId(dismissal.id);
    setError("");
    try {
      await restoreDismissedDuplicate(dismissal.id);
      setDismissed((current) => current.filter((row) => row.id !== dismissal.id));
      onRestored();
    } catch (err) {
      setError(getSafeErrorMessage(err, "Failed to restore this pair"));
    } finally {
      setRestoringId(null);
    }
  };

  if (isLoading) {
    return <p className="duplicates__empty">Loading dismissed duplicates…</p>;
  }

  return (
    <div className="duplicates__dismissed">
      {error && (
        <p className="duplicates__row-error" role="alert">
          {error}
        </p>
      )}

      {dismissed.length === 0 ? (
        <p className="duplicates__empty">No dismissed duplicates.</p>
      ) : (
        <ul className="duplicates__dismissed-list">
          {dismissed.map((row) => (
            <li key={row.id} className="duplicates__dismissed-row">
              <span className="duplicates__dismissed-names">
                {memberName(row.prospectA)} &amp; {memberName(row.prospectB)}
              </span>
              <span className="duplicates__dismissed-meta">
                Dismissed {new Date(row.dismissedAt).toLocaleDateString()}
                {row.dismissedByName ? ` by ${row.dismissedByName}` : ""}
              </span>
              <Button
                variant="secondary"
                loading={restoringId === row.id}
                onClick={() => handleRestore(row)}
              >
                Restore
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
